<template>
  <div v-if="isVisible" class="cpv-overlay" @click.self="close" role="dialog" aria-modal="true">
    <div class="cpv-dialog">

      <!-- ═══ HEADER ═══ -->
      <div class="cpv-head">
        <div class="cpv-head-left">
          <div class="cpv-head-icon"><i class="ri-file-list-3-line"></i></div>
          <div>
            <div class="cpv-head-title">Ventes comptabilisées</div>
            <div class="cpv-head-sub">
              <span class="cpv-chip">{{ ventes.length }} facture{{ ventes.length > 1 ? 's' : '' }}</span>
              <span class="cpv-head-note">Journal local - rétention 9 jours</span>
            </div>
          </div>
        </div>
        <button class="cpv-close" @click="close" aria-label="Fermer"><i class="ri-close-line"></i></button>
      </div>

      <!-- ═══ BARRE OUTILS ═══ -->
      <div class="cpv-toolbar">
        <div class="cpv-field">
          <label class="cpv-lbl">Journée de caisse</label>
          <select v-model="jourSelectionne" class="cpv-sel" @change="onChangeJour">
            <option v-if="!journees.length" value="">Aucune journée enregistrée</option>
            <option v-for="j in journees" :key="j.caissejour" :value="j.caissejour">
              {{ formatJour(j.caissejour) }} - {{ j.nbFactures }} facture(s)
            </option>
          </select>
        </div>
        <div class="cpv-search-wrap">
          <i class="ri-search-line cpv-search-icon"></i>
          <input v-model="search" type="search" class="cpv-search"
                 placeholder="N° document, client, table, serveur..." />
        </div>
        <button class="cpv-btn-sec" @click="charger" :disabled="isLoading">
          <i :class="isLoading ? 'ri-loader-4-line cpv-spin' : 'ri-refresh-line'"></i> Rafraîchir
        </button>
        <button class="cpv-btn-pri" @click="rapprocher" :disabled="isLoading || isRapprochant || !jourSelectionne">
          <i :class="isRapprochant ? 'ri-loader-4-line cpv-spin' : 'ri-scales-3-line'"></i>
          Rapprocher avec le serveur
        </button>
      </div>

      <!-- ═══ TOTAUX ═══ -->
      <div class="cpv-totaux">
        <span class="cpv-tot">Factures : <strong>{{ totaux.nbFactures }}</strong></span>
        <span class="cpv-tot">Total TTC : <strong class="cpv-ttc">{{ money(totaux.totalTTC) }}</strong></span>
        <span class="cpv-tot">Total HT : <strong>{{ money(totaux.totalHT) }}</strong></span>
        <span class="cpv-tot">Remises : <strong class="cpv-rem">{{ money(totaux.totalRemise) }}</strong></span>
      </div>

      <!-- ═══ BILAN DU RAPPROCHEMENT ═══ -->
      <transition name="cpv-fade">
        <div v-if="rapprochement" class="cpv-recap" :class="rapprochement.conforme ? 'cpv-recap-ok' : 'cpv-recap-ko'">
          <i :class="rapprochement.conforme ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'"></i>
          <span v-if="rapprochement.conforme">
            Journal local et serveur concordent - {{ rapprochement.okCount }} facture(s) vérifiée(s),
            écart de total nul.
          </span>
          <span v-else>
            <strong>{{ rapprochement.ecartsCount }} écart(s)</strong> - {{ rapprochement.absentServeur }} absente(s) du serveur,
            {{ rapprochement.absentLocal }} absente(s) du journal local,
            {{ rapprochement.montantDivergent }} montant(s) divergent(s).
            Écart de total : <strong>{{ money(rapprochement.ecartTotal) }}</strong>
          </span>
          <button class="cpv-recap-x" @click="rapprochement = null"><i class="ri-close-line"></i></button>
        </div>
      </transition>

      <!-- ═══ BODY ═══ -->
      <div class="cpv-body">
        <div v-if="isLoading" class="cpv-state">
          <i class="ri-loader-4-line cpv-spin cpv-state-icon"></i>
          <p>Lecture du journal local…</p>
        </div>

        <table v-else class="cpv-table">
          <thead>
            <tr>
              <th class="cpv-th cpv-th-num">N°</th>
              <th class="cpv-th">N° serveur</th>
              <th class="cpv-th">N° local</th>
              <th class="cpv-th cpv-hide-sm">Table</th>
              <th class="cpv-th cpv-hide-sm">Serveur</th>
              <th class="cpv-th">Client</th>
              <th class="cpv-th cpv-th-right">Montant TTC</th>
              <th class="cpv-th cpv-th-right cpv-hide-sm">Remise</th>
              <th class="cpv-th cpv-th-center">Lignes</th>
              <th class="cpv-th cpv-hide-sm">Heure</th>
              <th class="cpv-th cpv-th-center">Serveur ?</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="filtered.length">
              <template v-for="(v, i) in filtered" :key="v.k">
                <tr class="cpv-tr" :class="rowClass(v)" @click="toggleDetail(v)">
                  <td class="cpv-td"><span class="cpv-num">{{ i + 1 }}</span></td>
                  <td class="cpv-td"><span class="cpv-mono">{{ v.srvdocnum }}</span></td>
                  <td class="cpv-td"><span class="cpv-mono cpv-muted">{{ v.pidindexedb || '-' }}</span></td>
                  <td class="cpv-td cpv-hide-sm">
                    <span v-if="v.commandetable" class="cpv-table-badge">{{ v.commandetable }}</span>
                    <span v-else class="cpv-dash">-</span>
                  </td>
                  <td class="cpv-td cpv-hide-sm cpv-muted">{{ v.serveur || '-' }}</td>
                  <td class="cpv-td">{{ v.client || '-' }}</td>
                  <td class="cpv-td cpv-td-right"><span class="cpv-amount">{{ money(v.mtnt.docmtnttotalttc) }}</span></td>
                  <td class="cpv-td cpv-td-right cpv-hide-sm cpv-muted">{{ money(v.mtnt.docmtntremise) }}</td>
                  <td class="cpv-td cpv-td-center">
                    <span class="cpv-lignes">{{ v.nbLignes }}</span>
                  </td>
                  <td class="cpv-td cpv-hide-sm cpv-muted">{{ formatHeure(v.comptabiliseAt) }}</td>
                  <td class="cpv-td cpv-td-center">
                    <span v-if="!rapprochement" class="cpv-dash">-</span>
                    <span v-else class="cpv-badge" :class="'cpv-rp-' + (v._rapp || 'inconnu')" :title="v._rappDetail">
                      {{ rappLabel(v._rapp) }}
                    </span>
                  </td>
                </tr>
                <tr v-if="detailOuvert === v.k" :key="v.k + '-d'" class="cpv-detail-row">
                  <td colspan="11">
                    <div class="cpv-detail">
                      <div v-if="chargementDetail" class="cpv-detail-load">
                        <i class="ri-loader-4-line cpv-spin"></i> Déchiffrement des lignes…
                      </div>
                      <template v-else-if="detailItems.length">
                        <div class="cpv-detail-title">Articles de la facture {{ v.srvdocnum }}</div>
                        <table class="cpv-subtable">
                          <thead>
                            <tr><th>Désignation</th><th class="cpv-th-right">Qté</th><th class="cpv-th-right">P.U.</th><th class="cpv-th-right">Montant</th></tr>
                          </thead>
                          <tbody>
                            <tr v-for="(a, ai) in detailItems" :key="ai">
                              <td>{{ nomArticle(a) }}</td>
                              <td class="cpv-td-right">{{ a.qtite ?? a.qte ?? '-' }}</td>
                              <td class="cpv-td-right">{{ money(a.pu ?? a.prix) }}</td>
                              <td class="cpv-td-right">{{ money(montantLigne(a)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </template>
                      <div v-else class="cpv-detail-load">Aucune ligne enregistrée.</div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
            <tr v-else>
              <td colspan="11">
                <div class="cpv-state">
                  <i class="ri-file-list-3-line cpv-state-icon"></i>
                  <p>{{ ventes.length ? 'Aucun résultat' : 'Aucune vente comptabilisée pour cette journée' }}</p>
                </div>
              </td>
            </tr>

            <!-- Documents présents au serveur mais absents du journal local -->
            <template v-if="manquantsLocal.length">
              <tr class="cpv-sep-row"><td colspan="11">
                <i class="ri-error-warning-line"></i>
                Présent{{ manquantsLocal.length > 1 ? 's' : '' }} au serveur, absent{{ manquantsLocal.length > 1 ? 's' : '' }} du journal local
                ({{ manquantsLocal.length }})
              </td></tr>
              <tr v-for="m in manquantsLocal" :key="'srv-' + m.numdoc" class="cpv-tr cpv-tr-orphan">
                <td class="cpv-td"><i class="ri-cloud-line"></i></td>
                <td class="cpv-td"><span class="cpv-mono">{{ m.numdoc }}</span></td>
                <td class="cpv-td cpv-dash">-</td>
                <td class="cpv-td cpv-hide-sm cpv-dash">-</td>
                <td class="cpv-td cpv-hide-sm cpv-dash">-</td>
                <td class="cpv-td">{{ m.client || '-' }}</td>
                <td class="cpv-td cpv-td-right"><span class="cpv-amount">{{ money(m.mtntttc) }}</span></td>
                <td class="cpv-td cpv-td-right cpv-hide-sm cpv-muted">{{ money(m.mtntremise) }}</td>
                <td class="cpv-td cpv-td-center cpv-dash">-</td>
                <td class="cpv-td cpv-hide-sm cpv-muted">{{ m.creer_le || '-' }}</td>
                <td class="cpv-td cpv-td-center">
                  <span class="cpv-badge cpv-rp-absentLocal">Absent local</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- ═══ FOOTER ═══ -->
      <div class="cpv-foot">
        <div class="cpv-foot-info">
          <span v-if="rapprochement">
            Serveur : <strong>{{ rapprochement.nbServeur }}</strong> doc. /
            {{ money(rapprochement.totalServeur) }} - Local : <strong>{{ rapprochement.nbLocal }}</strong> doc. /
            {{ money(rapprochement.totalLocal) }}
          </span>
          <span v-else>Cliquez une ligne pour voir ses articles.</span>
        </div>
        <button class="cpv-btn-sec" @click="close">Fermer</button>
      </div>

    </div>
  </div>
</template>

<script>
const axios = require('axios');
import { currencyFormatDE, randomString } from '@/allfunction';
import * as JwtService from '@/common/jwt.service';
import {
  getJournees, getVentesDuJour, getTotauxDuJour, getVenteParDoc, toJourISO,
} from '@/service/comptabilisationVenteService';

export default {
  /* eslint-disable */
  name: 'modal_comptabilisationvente',

  data() {
    return {
      isVisible: false,
      isLoading: false,
      isRapprochant: false,
      journees: [],
      jourSelectionne: '',
      ventes: [],
      totaux: { nbFactures: 0, totalTTC: 0, totalHT: 0, totalRemise: 0 },
      search: '',
      detailOuvert: null,
      detailItems: [],
      chargementDetail: false,
      rapprochement: null,
      manquantsLocal: [],
    };
  },

  computed: {
    pidcaisse() {
      const ci = this.$store.state.caisseinfo || {};
      return String(ci.cpid || this.$store.state.caisseencour || '');
    },

    filtered() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.ventes;
      return this.ventes.filter(v =>
        String(v.srvdocnum     || '').toLowerCase().includes(q) ||
        String(v.pidindexedb   || '').toLowerCase().includes(q) ||
        String(v.client        || '').toLowerCase().includes(q) ||
        String(v.commandetable || '').toLowerCase().includes(q) ||
        String(v.serveur       || '').toLowerCase().includes(q)
      );
    },
  },

  methods: {
    show() {
      this.isVisible = true;
      this.search = '';
      this.rapprochement = null;
      this.manquantsLocal = [];
      this.detailOuvert = null;
      document.body.style.overflow = 'hidden';
      this.init();
    },

    close() {
      this.isVisible = false;
      document.body.style.overflow = '';
    },

    async init() {
      this.isLoading = true;
      try {
        this.journees = await getJournees(this.pidcaisse);
        const ci = this.$store.state.caisseinfo || {};
        const jourCourant = toJourISO(ci.dateouverture);
        // Journée de caisse en cours si elle a des ventes, sinon la plus récente
        this.jourSelectionne = this.journees.some(j => j.caissejour === jourCourant)
          ? jourCourant
          : (this.journees[0] ? this.journees[0].caissejour : '');
      } catch (e) {
        console.error('[comptabilisation] Lecture des journées impossible', e);
        this.journees = [];
      } finally {
        this.isLoading = false;
      }
      await this.charger();
    },

    onChangeJour() {
      this.rapprochement = null;
      this.manquantsLocal = [];
      this.detailOuvert = null;
      this.charger();
    },

    async charger() {
      if (!this.jourSelectionne) {
        this.ventes = [];
        this.totaux = { nbFactures: 0, totalTTC: 0, totalHT: 0, totalRemise: 0 };
        return;
      }
      this.isLoading = true;
      try {
        this.ventes = await getVentesDuJour(this.pidcaisse, this.jourSelectionne, false);
        this.totaux = await getTotauxDuJour(this.pidcaisse, this.jourSelectionne);
      } catch (e) {
        console.error('[comptabilisation] Lecture impossible', e);
        this.ventes = [];
        toastr['error']('Lecture du journal local impossible.', '<b>Erreur</b>');
      } finally {
        this.isLoading = false;
      }
    },

    async toggleDetail(v) {
      if (this.detailOuvert === v.k) { this.detailOuvert = null; return; }
      this.detailOuvert = v.k;
      this.detailItems = [];
      this.chargementDetail = true;
      try {
        const complet = await getVenteParDoc(this.pidcaisse, this.jourSelectionne, v.srvdocnum, true);
        this.detailItems = (complet && complet.items) || [];
        /* Les lignes archivées sont celles du ticket (listedatatable) : la
           désignation vit dans item.article.libelle, pas à la racine. */
      } catch (e) {
        console.error('[comptabilisation] Détail illisible', e);
        this.detailItems = [];
      } finally {
        this.chargementDetail = false;
      }
    },

    /* ══════════════════════════════════════════════════════════════
       Rapprochement local ↔ serveur.

       Réutilise l'endpoint des facturations de la période déjà en
       service (rapport FVC), interrogé sur la seule journée de caisse
       sélectionnée : aucune modification du backend n'est nécessaire.

       Appariement sur le numéro de document serveur. Les bons de
       retour (montant négatif) sont écartés : ils annulent une vente
       et n'ont pas de contrepartie dans le journal local.
       ══════════════════════════════════════════════════════════════ */
    async rapprocher() {
      this.isRapprochant = true;
      this.manquantsLocal = [];
      try {
        const jour = this.jourSelectionne;
        const url = process.env.VUE_APP_API_URL
          + '/egl74587kjkjrfdkcaisse771714dfe/c/rapport/etatc/FVC/'
          + jour + '/' + jour + '/' + this.pidcaisse + '/'
          + this.$store.state.appsociete
          + '/8954fddpap18rapportcaisse519151911515/' + randomString();

        const res = await axios.get(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + JwtService.getToken(),
            'csrftoken': this.$store.state.appsociete,
          },
          withCredentials: true,
        });

        if (!res.data || !res.data.success) {
          toastr['error'](res.data && res.data.message || 'Le serveur n\'a pas répondu.', '<b>Rapprochement</b>');
          return;
        }

        const docsServeur = (res.data.m || []).filter(d => parseFloat(d.mtntttc || 0) >= 0);
        const parNumdoc = new Map();
        docsServeur.forEach(d => parNumdoc.set(String(d.numdoc || '').trim(), d));

        let absentServeur = 0, montantDivergent = 0, okCount = 0;

        this.ventes.forEach(v => {
          const doc = parNumdoc.get(String(v.srvdocnum || '').trim());
          if (!doc) {
            v._rapp = 'absentServeur';
            v._rappDetail = 'Comptabilisée localement mais introuvable au serveur pour cette journée';
            absentServeur++;
            return;
          }
          const local  = parseFloat(v.mtnt.docmtnttotalttc || 0) || 0;
          const distant = parseFloat(doc.mtntttc || 0) || 0;
          // Tolérance 1 unité monétaire : les arrondis d'affichage ne sont pas des écarts
          if (Math.abs(local - distant) > 1) {
            v._rapp = 'divergent';
            v._rappDetail = `Local ${local} / serveur ${distant}`;
            montantDivergent++;
          } else {
            v._rapp = 'ok';
            v._rappDetail = 'Concordant';
            okCount++;
          }
          parNumdoc.delete(String(v.srvdocnum || '').trim());
        });

        // Ce qui reste dans la map n'a pas de contrepartie locale
        this.manquantsLocal = Array.from(parNumdoc.values());

        const totalLocal   = this.ventes.reduce((s, v) => s + (parseFloat(v.mtnt.docmtnttotalttc || 0) || 0), 0);
        const totalServeur = docsServeur.reduce((s, d) => s + (parseFloat(d.mtntttc || 0) || 0), 0);
        const ecartsCount  = absentServeur + montantDivergent + this.manquantsLocal.length;

        this.rapprochement = {
          conforme: ecartsCount === 0 && Math.abs(totalLocal - totalServeur) <= 1,
          okCount, absentServeur, montantDivergent,
          absentLocal: this.manquantsLocal.length,
          ecartsCount,
          nbLocal: this.ventes.length,
          nbServeur: docsServeur.length,
          totalLocal, totalServeur,
          ecartTotal: totalServeur - totalLocal,
        };

        if (this.rapprochement.conforme) {
          toastr['success']('Journal local et serveur concordent.', '<b>Rapprochement</b>');
        } else {
          toastr['warning'](ecartsCount + ' écart(s) détecté(s).', '<b>Rapprochement</b>');
        }
      } catch (e) {
        console.error('[comptabilisation] Rapprochement impossible', e);
        toastr['error']('Rapprochement impossible. Vérifiez la connexion.', '<b>Erreur</b>');
      } finally {
        this.isRapprochant = false;
      }
    },

    rowClass(v) {
      if (!this.rapprochement) return '';
      if (v._rapp === 'absentServeur') return 'cpv-tr-err';
      if (v._rapp === 'divergent')     return 'cpv-tr-warn';
      return '';
    },

    rappLabel(r) {
      return ({
        ok: 'Concordant', absentServeur: 'Absent serveur',
        divergent: 'Écart montant', absentLocal: 'Absent local',
      })[r] || '-';
    },

    money(v) { return currencyFormatDE(v || 0); },

    /* Désignation d'une ligne archivée.

       Les lignes proviennent de listedatatable : le nom du produit est porté
       par item.article.libelle. Les autres clés ne sont là que pour rester
       tolérant aux archives d'anciennes versions du journal. */
    nomArticle(a) {
      if (!a) return '-';
      const art = a.article || {};
      const nom = art.libelle || art.designation || art.name
               || a.designation || a.libelle || a.name || '';
      const complement = a.autredesignation ? ` (${a.autredesignation})` : '';
      return nom ? nom + complement : '-';
    },

    /* Montant de la ligne : calmtntht est le total ligne côté caisse. */
    montantLigne(a) {
      if (!a) return 0;
      return a.calmtntht ?? a.mtnt ?? a.montant ?? 0;
    },

    formatJour(iso) {
      if (!iso) return '-';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    },

    formatHeure(ts) {
      if (!ts) return '-';
      const d = new Date(ts);
      const p = (n) => String(n).padStart(2, '0');
      return `${p(d.getHours())}:${p(d.getMinutes())}`;
    },
  },

  beforeUnmount() {
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
.cpv-overlay {
  position: fixed; inset: 0; z-index: 10040;
  background: rgba(0,0,0,.55);
  display: flex; align-items: center; justify-content: center; padding: 16px;
  font-family: 'Segoe UI', system-ui, sans-serif;
}
.cpv-dialog {
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 14px; width: min(1200px, 100%);
  max-height: 92dvh; display: flex; flex-direction: column;
  overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,.45);
}

.cpv-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cpv-head-left { display: flex; align-items: center; gap: 10px; }
.cpv-head-icon {
  width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0;
  background: color-mix(in srgb, var(--green, #34d399) 15%, transparent);
  color: var(--green, #34d399); font-size: 18px;
  display: flex; align-items: center; justify-content: center;
}
.cpv-head-title { font-size: 14px; font-weight: 800; color: var(--text); }
.cpv-head-sub { margin-top: 2px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cpv-head-note { font-size: 10px; color: var(--text3); }
.cpv-chip {
  font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 20px;
  border: 1px solid var(--green, #34d399); color: var(--green, #34d399);
  background: color-mix(in srgb, var(--green, #34d399) 10%, transparent);
}
.cpv-close {
  background: none; border: none; color: var(--text2); font-size: 20px;
  cursor: pointer; padding: 4px; border-radius: 6px; flex-shrink: 0;
}
.cpv-close:hover { color: var(--red); }

.cpv-toolbar {
  display: flex; align-items: flex-end; gap: 8px; flex-wrap: wrap;
  padding: 10px 14px; border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cpv-field { display: flex; flex-direction: column; gap: 3px; }
.cpv-lbl { font-size: 10px; font-weight: 700; color: var(--text3); text-transform: uppercase; letter-spacing: .4px; }
.cpv-sel {
  padding: 8px 10px; border-radius: 7px; min-width: 220px;
  background: var(--surface2); border: 1px solid var(--border);
  color: var(--text); font-size: 12px; outline: none; cursor: pointer; font-family: inherit;
}
.cpv-search-wrap { position: relative; flex: 1; min-width: 170px; }
.cpv-search-icon {
  position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
  color: var(--text3); pointer-events: none; font-size: 15px;
}
.cpv-search {
  width: 100%; padding: 9px 12px 9px 32px;
  background: var(--surface2); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text); font-size: 13px; outline: none; font-family: inherit;
}
.cpv-search:focus { border-color: var(--amber); }

.cpv-totaux {
  display: flex; gap: 18px; flex-wrap: wrap;
  padding: 8px 18px; font-size: 12px; color: var(--text2);
  background: var(--surface2); border-bottom: 1px solid var(--border); flex-shrink: 0;
}
.cpv-tot strong { color: var(--text); }
.cpv-ttc { color: var(--green, #34d399) !important; }
.cpv-rem { color: var(--orange, #f59e0b) !important; }

.cpv-recap {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 18px; font-size: 12px; flex-shrink: 0;
  border-bottom: 1px solid var(--border);
}
.cpv-recap i { font-size: 16px; flex-shrink: 0; }
.cpv-recap-ok {
  color: var(--green, #34d399);
  background: color-mix(in srgb, var(--green, #34d399) 10%, transparent);
}
.cpv-recap-ko {
  color: var(--orange, #f59e0b);
  background: color-mix(in srgb, var(--orange, #f59e0b) 10%, transparent);
}
.cpv-recap-x {
  margin-left: auto; background: none; border: none; cursor: pointer;
  color: inherit; opacity: .6; font-size: 15px; padding: 0;
}
.cpv-recap-x:hover { opacity: 1; }
.cpv-fade-enter-active, .cpv-fade-leave-active { transition: opacity .2s, max-height .2s; max-height: 60px; overflow: hidden; }
.cpv-fade-enter-from, .cpv-fade-leave-to { opacity: 0; max-height: 0; }

.cpv-body { flex: 1; overflow: auto; }
.cpv-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 60px 20px; text-align: center; gap: 10px;
}
.cpv-state-icon { font-size: 40px; color: var(--text3); }
.cpv-state p { font-size: 14px; font-weight: 600; color: var(--text2); }
.cpv-spin { animation: cpv-rot .9s linear infinite; display: inline-block; }
@keyframes cpv-rot { to { transform: rotate(360deg); } }

.cpv-table { width: 100%; border-collapse: collapse; }
.cpv-th {
  padding: 10px 10px; text-align: left; font-size: 10px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .5px; color: var(--text2);
  background: color-mix(in srgb, var(--green, #34d399) 8%, var(--surface2));
  border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 1;
  white-space: nowrap;
}
.cpv-th-num { width: 38px; }
.cpv-th-right { text-align: right; }
.cpv-th-center { text-align: center; }
.cpv-tr { border-bottom: 1px solid var(--border); cursor: pointer; }
.cpv-tr:hover { background: color-mix(in srgb, var(--amber) 6%, var(--surface)); }
.cpv-tr-err  { background: color-mix(in srgb, var(--red, #f87171) 10%, transparent); }
.cpv-tr-warn { background: color-mix(in srgb, var(--orange, #f59e0b) 10%, transparent); }
.cpv-tr-orphan { background: color-mix(in srgb, var(--blue, #60a5fa) 8%, transparent); }
.cpv-td { padding: 9px 10px; font-size: 12px; color: var(--text); vertical-align: middle; }
.cpv-td-right { text-align: right; }
.cpv-td-center { text-align: center; }
.cpv-num {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%; font-size: 10px; font-weight: 800;
  background: var(--surface2); color: var(--text2);
}
.cpv-mono { font-family: ui-monospace, monospace; font-size: 11px; font-weight: 700; }
.cpv-muted { font-size: 11px; color: var(--text2); }
.cpv-dash { color: var(--text3); }
.cpv-amount { font-weight: 800; color: var(--green, #34d399); }
.cpv-table-badge {
  display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: 10px; font-weight: 700;
  background: color-mix(in srgb, var(--purple, #a78bfa) 12%, transparent); color: var(--purple, #a78bfa);
}
.cpv-lignes {
  display: inline-block; min-width: 20px; padding: 1px 6px; border-radius: 10px;
  font-size: 10px; font-weight: 800; background: var(--surface2); color: var(--text2);
}
.cpv-badge {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 10px; font-weight: 700; white-space: nowrap;
}
.cpv-rp-ok            { color: var(--green, #34d399);  background: color-mix(in srgb, var(--green, #34d399) 12%, transparent); }
.cpv-rp-absentServeur { color: var(--red, #f87171);    background: color-mix(in srgb, var(--red, #f87171) 14%, transparent); }
.cpv-rp-divergent     { color: var(--orange, #f59e0b); background: color-mix(in srgb, var(--orange, #f59e0b) 14%, transparent); }
.cpv-rp-absentLocal   { color: var(--blue, #60a5fa);   background: color-mix(in srgb, var(--blue, #60a5fa) 14%, transparent); }

.cpv-sep-row td {
  padding: 7px 14px; font-size: 11px; font-weight: 700;
  color: var(--blue, #60a5fa);
  background: color-mix(in srgb, var(--blue, #60a5fa) 12%, var(--surface2));
  border-top: 1px solid var(--border); border-bottom: 1px solid var(--border);
}
.cpv-sep-row i { margin-right: 5px; }

.cpv-detail-row td { padding: 0; background: var(--surface2); }
.cpv-detail { padding: 10px 16px 14px; }
.cpv-detail-title { font-size: 11px; font-weight: 800; color: var(--text2); margin-bottom: 6px; text-transform: uppercase; letter-spacing: .4px; }
.cpv-detail-load { font-size: 12px; color: var(--text3); padding: 6px 0; }
.cpv-subtable { width: 100%; border-collapse: collapse; }
.cpv-subtable th {
  text-align: left; font-size: 10px; font-weight: 700; color: var(--text3);
  padding: 4px 8px; border-bottom: 1px solid var(--border); text-transform: uppercase;
}
.cpv-subtable td { padding: 5px 8px; font-size: 11px; color: var(--text); border-bottom: 1px solid var(--border); }

.cpv-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 12px 14px; border-top: 1px solid var(--border); flex-shrink: 0;
  background: color-mix(in srgb, var(--surface2) 50%, var(--surface)); flex-wrap: wrap;
}
.cpv-foot-info { font-size: 12px; color: var(--text2); }
.cpv-foot-info strong { color: var(--text); }
.cpv-btn-sec, .cpv-btn-pri {
  padding: 8px 14px; border-radius: 8px; border: 1px solid var(--border);
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit;
  display: inline-flex; align-items: center; gap: 6px;
}
.cpv-btn-sec { background: var(--surface2); color: var(--text2); }
.cpv-btn-sec:hover:not(:disabled) { border-color: var(--amber); color: var(--amber); }
.cpv-btn-pri {
  background: color-mix(in srgb, var(--amber) 18%, transparent);
  border-color: var(--amber); color: var(--amber);
}
.cpv-btn-pri:hover:not(:disabled) { background: color-mix(in srgb, var(--amber) 28%, transparent); }
.cpv-btn-sec:disabled, .cpv-btn-pri:disabled { opacity: .4; cursor: not-allowed; }

.cpv-hide-sm { display: table-cell; }
@media (max-width: 860px) { .cpv-hide-sm { display: none; } }
@media (max-width: 640px) {
  .cpv-overlay { align-items: flex-end; padding: 0; }
  .cpv-dialog { width: 100%; max-height: 94dvh; border-radius: 16px 16px 0 0; }
  .cpv-sel { min-width: 0; width: 100%; }
  .cpv-field { flex: 1 1 100%; }
}
</style>
